import { useState } from 'react'
import { Select, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import {
  PrimaryUseEffectiveSource,
  FinancialAccountPrimaryUse,
  type FinancialAccount,
} from '@/reducers/finances/financialAccountsReducer'
import { updateAdminUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'
import { useAppDispatch } from '@/utils/typeHelpers'

const AUTOMATIC = 'automatic'

type PrimaryUseChoice = FinancialAccountPrimaryUse | typeof AUTOMATIC

const PRIMARY_USE_OPTIONS: { value: PrimaryUseChoice; text: string }[] = [
  { value: AUTOMATIC, text: 'Automatic' },
  { value: FinancialAccountPrimaryUse.BUSINESS, text: 'Business' },
  { value: FinancialAccountPrimaryUse.PERSONAL, text: 'Personal' },
]

const PRIMARY_USE_LABELS: Record<FinancialAccountPrimaryUse, string> = {
  [FinancialAccountPrimaryUse.BUSINESS]: 'Business',
  [FinancialAccountPrimaryUse.PERSONAL]: 'Personal',
}

const SOURCE_LABELS: Record<PrimaryUseEffectiveSource, string> = {
  [PrimaryUseEffectiveSource.BOOKKEEPER]: 'set by a bookkeeper',
  [PrimaryUseEffectiveSource.THERAPIST_ANSWER]: "from the therapist's answer",
  [PrimaryUseEffectiveSource.INFERRED]: 'inferred from sorting',
}

const isPrimaryUseChoice = (value: unknown): value is PrimaryUseChoice =>
  PRIMARY_USE_OPTIONS.some((option) => option.value === value)

const describePrimaryUseEffective = (account: FinancialAccount): string => {
  if (account.primaryUseEffective) {
    const source = account.primaryUseEffectiveSource
      ? `, ${SOURCE_LABELS[account.primaryUseEffectiveSource]}`
      : ''
    return `${PRIMARY_USE_LABELS[account.primaryUseEffective]}${source}`
  }
  // Matching falls back to the therapist's answer until a label is stored.
  if (account.primaryUse) {
    return `${PRIMARY_USE_LABELS[account.primaryUse]}, ${SOURCE_LABELS[PrimaryUseEffectiveSource.THERAPIST_ANSWER]}`
  }
  return 'No label, so transfers book as transfers'
}

interface PrimaryUseEffectiveFieldProps {
  account: FinancialAccount
  userId: number
}

export const PrimaryUseEffectiveField = ({
  account,
  userId,
}: PrimaryUseEffectiveFieldProps) => {
  const dispatch = useAppDispatch()
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const choice: PrimaryUseChoice =
    account.primaryUseEffectiveSource ===
      PrimaryUseEffectiveSource.BOOKKEEPER && account.primaryUseEffective
      ? account.primaryUseEffective
      : AUTOMATIC

  const handleChange = async (value: string) => {
    if (!isPrimaryUseChoice(value) || value === choice) return
    setSaving(true)
    setFailed(false)
    const saved = await updateAdminUserFinancialAccounts(userId, account.id, {
      primaryUseEffective: value === AUTOMATIC ? null : value,
    })(dispatch)
    setFailed(!saved)
    setSaving(false)
  }

  return (
    <div className="flex flex-col gap-1">
      <Text as={TypographyStyles.BODY_XS}>
        <b>Booking label:</b> {describePrimaryUseEffective(account)}
      </Text>
      <div className="max-w-[180px]">
        <Select
          value={choice}
          options={PRIMARY_USE_OPTIONS}
          onValueChange={handleChange}
          disabled={saving}
        />
      </div>
      {failed && (
        <Text as={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
          {"The booking label wasn't saved. Refresh the page and try again."}
        </Text>
      )}
    </div>
  )
}
